// EmployeeLeaveBalance.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './EmployeeLeaveBalance.css'; // Optional: for custom styles

const EmployeeLeaveBalance = ({ employeeId, show, onHide }) => {
    const [balances, setBalances] = useState([]);
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost/api/';

    const fetchLeaveBalance = useCallback(async () => {
        if (!employeeId) {
            setError('No employee selected');
            return;
        }

        try {
            setLoading(true);
            setError('');

            const token = localStorage.getItem('authToken');
            if (!token) {
                setError('Authentication token not found. Please log in again.');
                setLoading(false);
                return;
            }

            console.log('API URL:', `${API_BASE}leave/get_leave_balance.php?emp_id=${employeeId}`); // Debug log

            const response = await axios.get(
                `${API_BASE}leave/get_leave_balance.php?emp_id=${employeeId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    timeout: 10000 // 10 second timeout
                }
            );

            console.log('API Response:', response.data); // Debug log

            if (response.data.success) {
                setBalances(response.data.balances || []);
                setEmployee(response.data.employee || null);
            } else {
                setError(response.data.error || 'Failed to fetch leave balance');
            }
        } catch (error) {
            console.error('Fetch error:', error);

            if (error.response) {
                // Server responded with error
                setError(error.response.data?.error || `Error ${error.response.status}: ${error.response.statusText}`);
            } else if (error.request) {
                // Request made but no response
                setError('No response from server. Please check your network connection.');
            } else {
                // Other errors
                setError(error.message || 'Failed to fetch leave balance');
            }
        } finally {
            setLoading(false);
        }
    }, [employeeId, API_BASE]);

    useEffect(() => {
        if (show && employeeId) {
            console.log('Fetching leave balance for employee:', employeeId); // Debug log
            fetchLeaveBalance();
        } else {
            // Reset state when modal is hidden
            setBalances([]);
            setEmployee(null);
            setError('');
        }
    }, [show, employeeId, fetchLeaveBalance]);

    // Calculate total available days
    const totalAvailable = balances.reduce((sum, balance) => {
        const available = parseFloat(balance.available_balance) || 0;
        return sum + available;
    }, 0);

    if (!show) return null;

    return (
        <>
            <div className="modal-backdrop show"></div>
            <div className="modal show d-block" tabIndex="-1" style={{ overflowY: 'auto' }}>
                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header bg-primary text-white">
                            <h5 className="modal-title">
                                <i className="bi bi-calculator me-2"></i>
                                Leave Balance Summary
                            </h5>
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                onClick={onHide}
                                disabled={loading}
                                aria-label="Close"
                            ></button>
                        </div>

                        <div className="modal-body">
                            {error && (
                                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    {error}
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setError('')}
                                    ></button>
                                </div>
                            )}

                            {employee && (
                                <div className="card mb-4">
                                    <div className="card-header bg-light">
                                        <h6 className="mb-0">
                                            <i className="bi bi-person-badge me-2"></i>
                                            Employee Information
                                        </h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-6 mb-2">
                                                <strong>Name:</strong> {employee.first_name} {employee.last_name}
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <strong>Employee Code:</strong> {employee.emp_code}
                                            </div>
                                            <div className="col-md-6">
                                                <strong>Employee ID:</strong> {employee.emp_id}
                                            </div>
                                            <div className="col-md-6">
                                                <strong>Total Leave Types:</strong> {balances.length}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3">Loading leave balance...</p>
                                </div>
                            ) : balances.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-calculator display-1 text-muted"></i>
                                    <p className="mt-3 fs-5">No leave balance data available</p>
                                    <p className="text-muted">This employee may not have any leave allocations or ledger entries.</p>
                                    <button
                                        className="btn btn-primary mt-2"
                                        onClick={fetchLeaveBalance}
                                    >
                                        <i className="bi bi-arrow-clockwise me-2"></i>
                                        Try Again
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <table className="table table-hover table-striped">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Leave Type</th>
                                                    <th className="text-end">Earned</th>
                                                    <th className="text-end">Consumed</th>
                                                    <th className="text-end">Available</th>
                                                    <th className="text-end">Allocated</th>
                                                    <th>Utilization</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {balances.map((balance) => {
                                                    const earned = parseFloat(balance.total_earned) || 0;
                                                    const consumed = parseFloat(balance.total_consumed) || 0;
                                                    const available = parseFloat(balance.available_balance) || 0;
                                                    const allocated = parseFloat(balance.allocated_days) || 0;

                                                    const utilization = allocated > 0
                                                        ? ((consumed / allocated) * 100).toFixed(1)
                                                        : consumed > 0 ? '100.0' : '0.0';

                                                    return (
                                                        <tr key={balance.leave_type_id}>
                                                            <td>
                                                                <strong>{balance.leave_name}</strong>
                                                                <div className="small text-muted">{balance.leave_code}</div>
                                                            </td>
                                                            <td className="text-end text-success fw-bold">
                                                                {earned.toFixed(1)}
                                                            </td>
                                                            <td className="text-end text-danger fw-bold">
                                                                {consumed.toFixed(1)}
                                                            </td>
                                                            <td className="text-end">
                                                                <span className={`badge ${available > 0 ? 'bg-success' : 'bg-danger'}`}>
                                                                    {available.toFixed(1)} days
                                                                </span>
                                                            </td>
                                                            <td className="text-end">
                                                                {allocated > 0 ? (
                                                                    <span className="text-info">{allocated.toFixed(1)} days</span>
                                                                ) : 'N/A'}
                                                            </td>
                                                            <td>
                                                                {utilization !== 'N/A' ? (
                                                                    <div className="d-flex align-items-center">
                                                                        <div className="progress flex-grow-1 me-2" style={{ height: '20px' }}>
                                                                            <div
                                                                                className={`progress-bar ${parseFloat(utilization) > 80 ? 'bg-danger' : parseFloat(utilization) > 50 ? 'bg-warning' : 'bg-success'}`}
                                                                                role="progressbar"
                                                                                style={{ width: `${Math.min(utilization, 100)}%` }}
                                                                            >
                                                                                {utilization}%
                                                                            </div>
                                                                        </div>
                                                                        <small className="text-muted">{consumed.toFixed(1)}/{allocated.toFixed(1)}</small>
                                                                    </div>
                                                                ) : 'N/A'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                            <tfoot className="table-dark">
                                                <tr>
                                                    <th>Total</th>
                                                    <th className="text-end">
                                                        {balances.reduce((sum, b) => sum + (parseFloat(b.total_earned) || 0), 0).toFixed(1)}
                                                    </th>
                                                    <th className="text-end">
                                                        {balances.reduce((sum, b) => sum + (parseFloat(b.total_consumed) || 0), 0).toFixed(1)}
                                                    </th>
                                                    <th className="text-end">
                                                        <span className="badge bg-primary fs-6">
                                                            {totalAvailable.toFixed(1)} days
                                                        </span>
                                                    </th>
                                                    <th className="text-end">
                                                        {balances.reduce((sum, b) => sum + (b.allocated_days ? parseFloat(b.allocated_days) : 0), 0).toFixed(1)}
                                                    </th>
                                                    <th></th>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>

                                    <div className="row mt-4">
                                        <div className="col-md-4 mb-3">
                                            <div className="card bg-light border-success">
                                                <div className="card-body text-center py-4">
                                                    <div className="display-5 fw-bold text-success">
                                                        {totalAvailable.toFixed(1)}
                                                    </div>
                                                    <div className="text-muted">Total Available Days</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <div className="card bg-light border-primary">
                                                <div className="card-body text-center py-4">
                                                    <div className="display-5 fw-bold text-primary">
                                                        {balances.length}
                                                    </div>
                                                    <div className="text-muted">Leave Types</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <div className="card bg-light border-info">
                                                <div className="card-body text-center py-4">
                                                    <div className="display-5 fw-bold text-info">
                                                        {balances.filter(b => (parseFloat(b.available_balance) || 0) > 0).length}
                                                    </div>
                                                    <div className="text-muted">Types with Balance</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onHide}
                                disabled={loading}
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={fetchLeaveBalance}
                                disabled={loading}
                            >
                                <i className="bi bi-arrow-clockwise me-2"></i>
                                Refresh
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                    // Optional: Add print functionality
                                    window.print();
                                }}
                            >
                                <i className="bi bi-printer me-2"></i>
                                Print
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EmployeeLeaveBalance;